import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";

export interface AccountTarget { id: string; displayName: string | null }

interface Props {
  title: string;
  scope: string;
  target: AccountTarget;
  pending: boolean;
  changed: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

export function AccountActionDialog({ title, scope, target, pending, changed, onClose, children }: Props) {
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  useEffect(() => {
    const dialog = ref.current;
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog?.showModal();
    return () => {
      if (dialog?.open) dialog.close();
      if (opener.current?.isConnected) opener.current.focus();
    };
  }, []);

  function trapTab(e: React.KeyboardEvent<HTMLDialogElement>) {
    if (e.key !== "Tab") return;
    const dialog = ref.current;
    if (!dialog) return;
    const controls = [...dialog.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )].filter((element) => !element.matches(":disabled") && element.getClientRects().length > 0);
    if (!controls.length) { e.preventDefault(); return; }
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (e.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
      e.preventDefault(); first.focus();
    }
  }

  function requestClose() {
    if (pending) return;
    if (changed) { setConfirmDiscard(true); return; }
    onClose();
  }

  return (
    <dialog ref={ref} aria-label={title} onKeyDown={trapTab} onCancel={(e) => { e.preventDefault(); requestClose(); }}
      className="w-[min(42rem,calc(100vw-2rem))] max-h-[calc(100vh-2rem)] overflow-y-auto rounded-lg border border-border bg-card p-0 text-foreground shadow-2xl backdrop:bg-black/70">
      <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-border bg-card px-5 py-4">
        <h2 className="text-lg font-semibold">{title}</h2>
        <Button type="button" variant="ghost" size="sm" onClick={requestClose} disabled={pending} aria-label={`Close ${title}`}>Close</Button>
      </div>
      <div className="space-y-4 p-5">
        <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
          <div className="font-medium">{target.displayName ?? "Player"}</div>
          <div className="font-mono text-xs text-muted-foreground">{target.id}</div>
          <p className="mt-2 text-xs text-muted-foreground">{scope}</p>
        </div>
        {children}
        {confirmDiscard && <div role="alertdialog" aria-label="Discard action draft" className="rounded-md border border-amber-500/60 bg-amber-500/10 p-3 space-y-2">
          <p className="text-sm">Discard this action draft?</p>
          <div className="flex gap-2">
            <Button type="button" variant="destructive" onClick={onClose} disabled={pending}>Discard draft</Button>
            <Button type="button" variant="secondary" onClick={() => setConfirmDiscard(false)}>Keep editing</Button>
          </div>
        </div>}
        <Button type="button" variant="outline" onClick={requestClose} disabled={pending}>Cancel</Button>
      </div>
    </dialog>
  );
}
